import React, { createContext, createRef, useContext, useMemo, useState } from 'react';
import * as THREE from "three";

import { IProviderProps, ISceneDataContext } from '../@types/ContextProviderRelated';
import { DEFAULT_ZOOM_VALUE } from '../utils/constants';

const sceneDataContextDefaultValues: ISceneDataContext = {
    sceneNeedsClearing: false,
    setSceneNeedsClearing: () => { },
    zoomData: DEFAULT_ZOOM_VALUE,
    setZoomData: () => { },
    sceneRef: createRef(),
    setSceneRef: () => { },
    compassRotation: 0,
    setCompassRotation: () => { },
    rotateSceneContents: 0,
    setRotateSceneContents: () => {}
};

export const SceneDataContext = createContext(sceneDataContextDefaultValues);

export const useSceneData = () => useContext(SceneDataContext);

export const SceneDataProvider = ({ children }: IProviderProps): JSX.Element => {
    // Indicates if the scene has to get cleared and allocated memory in the renderer needs to get released.
    const [sceneNeedsClearing, setSceneNeedsClearing] = useState<boolean>(sceneDataContextDefaultValues.sceneNeedsClearing);
    // A readonly copy of the cameras zoom data, does not set the actual zoom.
    const [zoomData, setZoomData] = useState<number>(sceneDataContextDefaultValues.zoomData);
    // Reference to the scene.
    const [sceneRef, setSceneRef] = useState<React.RefObject<THREE.Scene>>(sceneDataContextDefaultValues.sceneRef);
    // Compass rotation in degrees (0-359).
    const [compassRotation, setCompassRotation] = useState<number>(sceneDataContextDefaultValues.compassRotation);
    // Needed rotation of scene elements in degrees. (-359 - 359)
    const [rotateSceneContents, setRotateSceneContents] = useState<number>(sceneDataContextDefaultValues.rotateSceneContents);
    const value = useMemo<ISceneDataContext>(() => ({
        sceneNeedsClearing,
        setSceneNeedsClearing,
        zoomData,
        setZoomData,
        sceneRef,
        setSceneRef,
        compassRotation,
        setCompassRotation,
        rotateSceneContents,
        setRotateSceneContents
    }), [
        sceneNeedsClearing,
        zoomData,
        sceneRef,
        compassRotation,
        rotateSceneContents
    ]);

    return (
        <SceneDataContext.Provider value={value}>
            {children}
        </SceneDataContext.Provider>
    )
}
